import { AbilityBuilder, RawRuleOf, createMongoAbility, detectSubjectType } from "@casl/ability";
import { z } from "zod";

import { LoggerManager } from "@archipad-js/core/logger";

import { BaseEntity } from "@core/services/orm/orm";
import { query } from "@core/services/orm/query";

import { KnownProjectTemplate } from "@archipad/backend/project/projectTemplateManager";
import { AbilityGroupType, ArchipadAbility } from "@archipad-js/archipad/entity-services";

import { ProjectEntity, ProjectUserEntity } from "@archipad-js/archipad-model";
import { LegacyUserInformation } from "./account.service";
import { AccountPlan } from "./account.service.types";

const logger = LoggerManager.getLogger("ArchipadAbilityFactory");

export function makeArchipadAbility(): ArchipadAbility {
    return createMongoAbility([], {
        detectSubjectType: (subject) => {
            if (isEntity(subject)) {
                return `${AbilityGroupType.entity}_${subject.entityType.entityName as keyof EntityTypeMap}`;
            }

            return detectSubjectType(subject);
        },
    });
}

/**
 * @see {@link ProjectUserRightEntity}
 */
const KnownProjectUserRights = z.enum([
    /**
     * Base => Administrateur
     * Promoimmo => Promoteur
     * Cogedim => RC
     */
    "administrator",

    /**
     * Base => Collaborateur
     * Promoimmo => Collaborateur
     * Cogedim => Collaborateur
     */
    "collaborator",

    /**
     * Base => Invité
     * Promoimmo => Invité
     * Cogedim => Invité
     */
    "guest",

    /**
     * Base => Lot de travaux
     * Promoimmo => Lot de travaux
     * Cogedim => Lot de travaux
     */
    "workpackage",

    /**
     * Promoimmo => Responsable clientèle
     * Cogedim => RRC
     */
    "client",

    /**
     * Promoimmo => Collaborateur chantier
     */
    "collaborator_without_real_estate_dev",

    /**
     * Promoimmo => Maître d'ouvrage
     */
    "guest_client",

    /**
     * Promoimmo => Responsable SAV
     * Cogedim => Responsable SAV
     */
    "afterSalesServiceManager",
]);

export type KnownProjectUserRights = z.infer<typeof KnownProjectUserRights>;

const KnownProjectUserRoles = z.enum([
    /**
     * BASE TEMPLATE ==> Administrateur
     */
    "admin",

    /**
     * BASE TEMPLATE ==> Maitre d'oeuvre
     * Cogedim ==> RC
     */
    "ownerAssistant",

    /**
     * BASE TEMPLATE ==> Invité
     */
    "guest",

    /**
     * BASE TEMPLATE ==> Lot de travaux
     */
    "workpackage",

    /**
     * WORKFLOW COMPLEX ==> Client,
     * COGEDIM ==> RRC
     */
    "client",

    /**
     * WORKFLOW COMPLEX ==> After Sales Service Manager,
     */
    "afterSalesServiceManager",

    /**
     * PROMOIMMO ==> Collaborateur chantier
     */
    "ownerAssistantWithoutHandover",

    /**
     * PROMOIMMO ==> Maître d'ouvrage
     */
    "guestClient",

    /**
     * COGEDIM ==> Collaborateur
     */
    "collaborator",
]);

export type KnownProjectUserRoles = z.infer<typeof KnownProjectUserRoles>;

export function computeArchipadAbilityRules(
    authInfo: LegacyUserInformation | null,
    accountInfo: AccountPlan | null,
    project: ProjectEntity | null,
): RawRuleOf<ArchipadAbility>[] {
    const abilityBuilder = new AbilityBuilder<ArchipadAbility>(createMongoAbility);

    if (!authInfo || !accountInfo || !project) {
        console.warn("Unable to compute Archipad ability rules without authInfo, accountInfo or project");
        return abilityBuilder.rules;
    }

    /**
     * @TODO: Duplicate code with getCurrentUser from workflowUserManager
     * Should be refactored in UserService between ORM Service and facade Service
     */
    const entityContext = project?.getContext();
    const ormQuery = query<ProjectUserEntity>(entityContext, "ProjectUser");
    ormQuery.predicateWithFormat("cloudUserId == {cloudUserId}");
    const predicateParams = { cloudUserId: authInfo.userId.toString() };
    const currentUser = ormQuery.execute(predicateParams).firstEntity();

    const currentUserRightTemplateName = currentUser?.right?.templateName;
    const currentUserRoleTemplateName = currentUser?.role?.templateName;
    const knownProjectUserRight = KnownProjectUserRights.safeParse(currentUserRightTemplateName);
    const knownProjectUserRole = KnownProjectUserRoles.safeParse(currentUserRoleTemplateName);

    if (knownProjectUserRight.success && knownProjectUserRole.success) {
        computePagesAccessRules(abilityBuilder, knownProjectUserRight.data);
        computeEntitiesAccessRules(abilityBuilder, knownProjectUserRight.data, knownProjectUserRole.data, currentUser);
        computeSectionAccessRules(abilityBuilder, knownProjectUserRight.data);
        computeProjectSettingsRules(abilityBuilder, knownProjectUserRight.data);
    } else {
        logger.warn(`Unknown role template name "${currentUserRoleTemplateName}"`);
        abilityBuilder.can("manage", "all");
    }

    const rules: RawRuleOf<ArchipadAbility>[] = abilityBuilder.rules;
    return rules;
}

function computePagesAccessRules(
    abilityBuilder: AbilityBuilder<ArchipadAbility>,
    knownProjectUserRight: KnownProjectUserRights,
): void {
    switch (knownProjectUserRight) {
        case KnownProjectUserRights.enum.administrator:
        case KnownProjectUserRights.enum.collaborator:
        case KnownProjectUserRights.enum.client:
        case KnownProjectUserRights.enum.afterSalesServiceManager:
        case KnownProjectUserRights.enum.guest_client:
        case KnownProjectUserRights.enum.guest: {
            abilityBuilder.can("read", "page_sav");
            break;
        }
    }

    switch (knownProjectUserRight) {
        case KnownProjectUserRights.enum.administrator:
        case KnownProjectUserRights.enum.collaborator:
        case KnownProjectUserRights.enum.collaborator_without_real_estate_dev:
            {
                abilityBuilder.can("read", "page_infos");
                abilityBuilder.can("read", "page_plans");
                abilityBuilder.can("read", "page_planning");
                abilityBuilder.can("read", "page_meetings");
                abilityBuilder.can("read", "page_issues");
                abilityBuilder.can("read", "page_remarks");
                abilityBuilder.can("read", "page_digitform");
                abilityBuilder.can("read", "page_reports");
                abilityBuilder.can("read", "page_dashboard");
            }
            break;
        case KnownProjectUserRights.enum.client:
            {
                abilityBuilder.can("read", "page_infos");
                abilityBuilder.can("read", "page_plans");
            }
            break;
        case KnownProjectUserRights.enum.afterSalesServiceManager:
            {
                abilityBuilder.can("read", "page_infos");
                abilityBuilder.can("read", "page_plans");
                abilityBuilder.can("read", "page_meetings");
                abilityBuilder.can("read", "page_issues");
                abilityBuilder.can("read", "page_reports");
                abilityBuilder.can("read", "page_dashboard");
            }
            break;
        case KnownProjectUserRights.enum.guest:
            {
                abilityBuilder.can("read", "page_infos");
                abilityBuilder.can("read", "page_plans");
                abilityBuilder.can("read", "page_planning");
                abilityBuilder.can("read", "page_meetings");
                abilityBuilder.can("read", "page_issues");
                abilityBuilder.can("read", "page_remarks");
                abilityBuilder.can("read", "page_digitform");
                abilityBuilder.can("read", "page_reports");
                abilityBuilder.can("read", "page_dashboard");
            }
            break;
        case KnownProjectUserRights.enum.workpackage: {
            abilityBuilder.can("read", "page_infos");
            abilityBuilder.can("read", "page_plans");
            abilityBuilder.can("read", "page_issues");
        }
    }
}

function computeEntitiesAccessRules(
    abilityBuilder: AbilityBuilder<ArchipadAbility>,
    knownProjectUserRight: KnownProjectUserRights,
    knownProjectUserRole: KnownProjectUserRoles,
    currentUser: ProjectUserEntity,
): void {
    switch (knownProjectUserRight) {
        case KnownProjectUserRights.enum.administrator:
        case KnownProjectUserRights.enum.collaborator:
        case KnownProjectUserRights.enum.collaborator_without_real_estate_dev:
        case KnownProjectUserRights.enum.afterSalesServiceManager:
        case KnownProjectUserRights.enum.client:
            {
                abilityBuilder.can("download", "entity_Document");
                abilityBuilder.can("update", "entity_Document");

                abilityBuilder.can("download", "entity_Report");
                abilityBuilder.can("share", "entity_Report");
            }
            break;
    }

    switch (knownProjectUserRight) {
        case KnownProjectUserRights.enum.administrator:
        case KnownProjectUserRights.enum.afterSalesServiceManager:
        case KnownProjectUserRights.enum.client:
            {
                abilityBuilder.can("create", "entity_RegionMember");
                abilityBuilder.can("update", "entity_RegionMember");
                abilityBuilder.can("delete", "entity_RegionMember");
            }
            break;
    }

    switch (knownProjectUserRight) {
        case KnownProjectUserRights.enum.administrator:
        case KnownProjectUserRights.enum.collaborator:
        case KnownProjectUserRights.enum.collaborator_without_real_estate_dev:
        case KnownProjectUserRights.enum.afterSalesServiceManager:
        case KnownProjectUserRights.enum.client:
            {
                abilityBuilder.can("create", "entity_Visit");
                abilityBuilder.can("update", "entity_Visit");
                abilityBuilder.can("delete", "entity_Visit");
            }
            break;
    }

    switch (knownProjectUserRight) {
        case KnownProjectUserRights.enum.administrator:
        case KnownProjectUserRights.enum.collaborator:
        case KnownProjectUserRights.enum.collaborator_without_real_estate_dev:
        case KnownProjectUserRights.enum.afterSalesServiceManager:
        case KnownProjectUserRights.enum.client:
            {
                abilityBuilder.can("update", "entity_Map");
            }
            break;
    }

    switch (knownProjectUserRole) {
        case KnownProjectUserRoles.enum.admin:
        case KnownProjectUserRoles.enum.afterSalesServiceManager:
        case KnownProjectUserRoles.enum.collaborator:
        case KnownProjectUserRoles.enum.client:
        case KnownProjectUserRoles.enum.guestClient:
        case KnownProjectUserRoles.enum.ownerAssistant:
        case KnownProjectUserRoles.enum.ownerAssistantWithoutHandover:
        case KnownProjectUserRoles.enum.workpackage:
            {
                abilityBuilder.can("create", "entity_Bug");
            }
            break;
    }

    switch (knownProjectUserRole) {
        case KnownProjectUserRoles.enum.admin:
        case KnownProjectUserRoles.enum.ownerAssistant:
        case KnownProjectUserRoles.enum.ownerAssistantWithoutHandover:
        case KnownProjectUserRoles.enum.collaborator:
        case KnownProjectUserRoles.enum.afterSalesServiceManager:
        case KnownProjectUserRoles.enum.client:
        case KnownProjectUserRoles.enum.guestClient:
            {
                abilityBuilder.can("update", "entity_BugNote", { "user.id": { $in: [currentUser.id, null] } });
            }
            break;
    }

    abilityBuilder.can("read", "entity_Document");
    abilityBuilder.can("read", "entity_Visit");
    abilityBuilder.can("read", "entity_Bug");

    if (knownProjectUserRight !== KnownProjectUserRights.enum.guest) {
        abilityBuilder.can("read", "entity_Report");
    }
}

function computeSectionAccessRules(
    abilityBuilder: AbilityBuilder<ArchipadAbility>,
    knownProjectUserRight: KnownProjectUserRights,
): void {
    switch (knownProjectUserRight) {
        case KnownProjectUserRights.enum.administrator:
            {
                abilityBuilder.can('update', "section_infos_building_program");
            }
            break;
        case KnownProjectUserRights.enum.collaborator:
        case KnownProjectUserRights.enum.collaborator_without_real_estate_dev:
        case KnownProjectUserRights.enum.afterSalesServiceManager:
        case KnownProjectUserRights.enum.client:
        case KnownProjectUserRights.enum.guest:
        case KnownProjectUserRights.enum.workpackage:
            break;
    }
}

function computeProjectSettingsRules(
    abilityBuilder: AbilityBuilder<ArchipadAbility>,
    knownProjectUserRight: KnownProjectUserRights,
): void {
    switch (knownProjectUserRight) {
        case KnownProjectUserRights.enum.administrator: {
            abilityBuilder.can("update", "projectSettings_settingSiteTalkAllowed");
            abilityBuilder.can("update", "projectSettings_settingBuyerNotificationAllowed");
            break;
        }
    }

    switch (knownProjectUserRight) {
        case KnownProjectUserRights.enum.administrator:
        case KnownProjectUserRights.enum.guest_client:
        case KnownProjectUserRights.enum.collaborator:
        case KnownProjectUserRights.enum.afterSalesServiceManager: {
            abilityBuilder.can("update", "projectSettings_settingSelfDeclarationAllowed");
            break;
        }
    }

    switch (knownProjectUserRight) {
        case KnownProjectUserRights.enum.administrator:
        case KnownProjectUserRights.enum.afterSalesServiceManager: {
            abilityBuilder.can("update", "projectSettings_settingRegionTypes");
            break;
        }
    }
}

function isEntity(o: unknown): o is BaseEntity {
    return o instanceof BaseEntity;
}

export function isCogedimProject(project: ProjectEntity): boolean {
    const isCogedimProject = project.templateVersions.some(
        (templateVersion) => templateVersion.projectTemplateId === KnownProjectTemplate.Cogedim,
    );
    return isCogedimProject;
}
